/**
 * ClaimGameHint Component
 * 
 * Shows a subtle hint to logged-in users who are playing an anonymous game,
 * asking if they want to add it to their account.
 * 
 * Only shows once per game. If dismissed, never asks again.
 */

import React from "react";
import { Alert, Button, Box } from "@mui/material";
import { Auth } from "aws-amplify";

interface ClaimGameHintProps {
  gameId: string;
  onClaim: () => Promise<void>;
}

export function ClaimGameHint({ gameId, onClaim }: ClaimGameHintProps) {
  const [show, setShow] = React.useState(false);
  const [claiming, setClaiming] = React.useState(false);

  React.useEffect(() => {
    checkShouldShow();
  }, [gameId]);

  async function checkShouldShow() {
    console.log("🔍 [ClaimGameHint] Checking if should show for game:", gameId);

    try {
      // Check if user is logged in
      const user = await Auth.currentAuthenticatedUser();
      const userId = user.attributes.sub;
      console.log("✅ [ClaimGameHint] User is logged in:", userId);

      // ------------------------------------------------------------
      // 1) Prefer anonymousGames tracking (legacy / anonymous flow)
      // ------------------------------------------------------------
      const { isMyAnonymousGame } = await import("../utils/anonymousGames");
      const isAnonymous = isMyAnonymousGame(gameId);
      console.log("🔍 [ClaimGameHint] Is anonymous game?", isAnonymous);

      // Check if already dismissed
      const dismissedJson = localStorage.getItem("dismissedClaims") || "[]";
      const dismissed: string[] = JSON.parse(dismissedJson);
      console.log("🔍 [ClaimGameHint] Dismissed claims:", dismissed);
      
      if (dismissed.includes(gameId)) {
        console.log("❌ [ClaimGameHint] Already dismissed - won't show");
        return; // User dismissed this hint
      }

      // Check if game is already claimed
      const { getGame } = await import("../api/gameApi");
      const game = await getGame(gameId);

      if (!game) {
        console.log("❌ [ClaimGameHint] Game not found - won't show");
        return; // Game not found at all
      }

      console.log("🔍 [ClaimGameHint] Game data:", {
        owner: game.owner,
        sharedWith: game.sharedWith,
      });

      // ------------------------------------------------------------
      // 2) If user already has access: nothing to claim
      // ------------------------------------------------------------
      if (game.owner === userId || game.sharedWith?.includes(userId)) {
        console.log("✅ [ClaimGameHint] User already has access - removing from anonymous games");
        // Already claimed, remove from anonymous games if present
        const { removeAnonymousGame } = await import(
          "../utils/anonymousGames"
        );
        removeAnonymousGame(gameId);
        return;
      }

      // ------------------------------------------------------------
      // 3) Decide WHEN to show the hint
      //
      //    a) Anonymous game created before login → isAnonymous === true
      //       → always show, user can "übernehmen"
      //
      //    b) Nicht-anonymes Spiel (z.B. du wurdest eingeladen),
      //       aber du spielst bereits von diesem Browser aus
      //       (localStorage-Eintrag <gameId>) → auch Claim anbieten.
      // ------------------------------------------------------------

      let isLocalPlayer = false;
      try {
        const localEntry = localStorage.getItem(gameId);
        if (localEntry) {
          const data = JSON.parse(localEntry);
          // Jede gültige player-Info reicht uns als Hinweis:
          if (data && (data.player === 1 || data.player === 2 || data.player === "1" || data.player === "2")) {
            isLocalPlayer = true;
          }
        }
      } catch (e) {
        console.warn("⚠️ [ClaimGameHint] Failed to read local player info:", e);
      }

      const shouldShow = isAnonymous || isLocalPlayer;
      console.log("🔍 [ClaimGameHint] shouldShow?", shouldShow, {
        isAnonymous,
        isLocalPlayer,
      });

      if (!shouldShow) {
        console.log("❌ [ClaimGameHint] Not anonymous and no local player info - won't show");
        return;
      }

      // Show hint!
      console.log("🎉 [ClaimGameHint] Showing claim hint!");
      setShow(true);
    } catch (error) {
      // Not logged in or error
      console.log("❌ [ClaimGameHint] Error or not logged in:", error);
      setShow(false);
    }
  }

  async function handleClaim() {
    setClaiming(true);
    try {
      await onClaim();
      setShow(false);

      // Remove from anonymous games
      const { removeAnonymousGame } = await import("../utils/anonymousGames");
      removeAnonymousGame(gameId);

      console.log("✅ Game claimed successfully");
    } catch (error) {
      console.error("❌ Failed to claim game:", error);
      setClaiming(false);
    }
  }

  function handleDismiss() {
    // Never ask again for this game
    const dismissedJson = localStorage.getItem("dismissedClaims") || "[]";
    const dismissed: string[] = JSON.parse(dismissedJson);
    dismissed.push(gameId);
    localStorage.setItem("dismissedClaims", JSON.stringify(dismissed));

    setShow(false);
    console.log(`ℹ️ Claim hint dismissed for game: ${gameId}`);
  }

  if (!show) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Alert
        severity="info"
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              color="inherit"
              size="small"
              onClick={handleClaim}
              disabled={claiming}
            >
              {claiming ? "..." : "Hinzufügen"}
            </Button>
            <Button color="inherit" size="small" onClick={handleDismiss}>
              Nicht jetzt
            </Button>
          </Box>
        }
      >
        Möchtest du dieses Spiel zu deinem Account hinzufügen?
      </Alert>
    </Box>
  );
}
